/**
 * React Query cache keys.
 *
 * Tenant-scoped data is keyed by the selected organization so switching orgs
 * never shows one customer's records under another's name while refetching.
 * Global plausibility tables (taxonomy, unknown labels, global priors) are
 * not org-scoped: they live in the admin database and read the same for
 * every organization.
 */
export const queryKeys = {
	costs: (orgCode: string, month: string) =>
		["shadow", "costs", orgCode, month] as const,
	installations: (orgCode: string) =>
		["shadow", "installations", orgCode] as const,
	me: ["shadow", "me"] as const,
	organizations: ["shadow", "organizations"] as const,
	plausibility: {
		calibrationProposals: (status: string) =>
			["shadow", "plausibility", "calibration", status] as const,
		globalPriors: ["shadow", "plausibility", "global-priors"] as const,
		priors: (orgCode: string) =>
			["shadow", "plausibility", "priors", orgCode] as const,
		settings: (orgCode: string) =>
			["shadow", "plausibility", "settings", orgCode] as const,
		taxonomy: ["shadow", "plausibility", "taxonomy"] as const,
		unknownLabels: (status: string) =>
			["shadow", "plausibility", "unknown-labels", status] as const,
		verification: (orgCode: string, verificationId: string) =>
			[
				"shadow",
				"plausibility",
				"verification",
				orgCode,
				verificationId,
			] as const,
		verifications: (orgCode: string, filters: Record<string, string>) =>
			["shadow", "plausibility", "verifications", orgCode, filters] as const,
		verificationVideo: (orgCode: string, verificationId: string) =>
			[
				"shadow",
				"plausibility",
				"verification-video",
				orgCode,
				verificationId,
			] as const,
	},
	reanalysis: {
		event: (orgCode: string, eventId: string) =>
			["shadow", "reanalysis", "event", orgCode, eventId] as const,
		eventVideo: (orgCode: string, eventId: string) =>
			["shadow", "reanalysis", "event-video", orgCode, eventId] as const,
		list: (orgCode: string, filters: Record<string, string>) =>
			["shadow", "reanalysis", "list", orgCode, filters] as const,
		/** The correction panel's context and the event's history, in one read. */
		view: (orgCode: string, eventId: string) =>
			["shadow", "reanalysis", "view", orgCode, eventId] as const,
	},
	sceneFacts: {
		proposal: (orgCode: string, proposalId: string) =>
			["shadow", "scene-facts", "proposal", orgCode, proposalId] as const,
		/** `statuses` is the comma-joined status filter, "" for every status. */
		proposals: (orgCode: string, statuses: string) =>
			["shadow", "scene-facts", "proposals", orgCode, statuses] as const,
		proposalVideo: (orgCode: string, proposalId: string) =>
			["shadow", "scene-facts", "proposal-video", orgCode, proposalId] as const,
	},
} as const;
